'use client'

import { useEffect, useState } from 'react'
import { cn } from '../lib/cn'

interface EmailConfirmationPopoverProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Popover component that displays a "Thanks for confirming your email" message
 * Shows when users arrive from the email confirmation link
 */
export function EmailConfirmationPopover({ isOpen, onClose }: EmailConfirmationPopoverProps) {
  const [isVisible, setIsVisible] = useState(isOpen)

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- needed to mount DOM before animating in
      setIsVisible(true)

      // Auto-close after 5 seconds
      const timer = setTimeout(() => {
        onClose()
      }, 5000)

      return () => clearTimeout(timer)
    } else {
      // Delay hiding to allow exit animation
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [isOpen, onClose])

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop with modern blur effect */}
      <div 
        className={cn(
          "fixed inset-0 z-40 transition-all duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        style={{
          backgroundColor: 'rgba(9, 20, 16, 0.3)',
          backdropFilter: 'blur(12px) saturate(60%)',
          WebkitBackdropFilter: 'blur(12px) saturate(60%)',
        }}
      />
      
      {/* Popover */}
      <div
        className={cn(
          "fixed z-50 transition-all duration-300 ease-out top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 w-full sm:w-auto",
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
        )}
      >
        <div className="bg-baby-powder rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-night/10 overflow-hidden w-full sm:w-[400px]">
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-6 right-6 z-10 w-8 h-8 flex items-center justify-center rounded-full hover:bg-night/5 transition-colors group"
            aria-label="Close"
          >
            <svg className="w-4 h-4 text-night/30 group-hover:text-night/50 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="p-8 py-12 text-center">
            <h3 className="text-h4 text-night font-display">
              Thanks for confirming your email!
            </h3>
          </div>
        </div>
      </div>
    </>
  )
}

